import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@/lib/api";

interface Fetcher<T> {
  (caseId: string): Promise<T[]>;
}

interface UseCollectionResult<T> {
  rows: T[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Fetches a worksheet line-item collection for a case and re-fetches on demand
 * (after a dialog create/update/delete). Same out-of-order/StrictMode guards as
 * `use-cases.ts`.
 */
export function useCollection<T>(caseId: string, list: Fetcher<T>): UseCollectionResult<T> {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const reqId = useRef(0);
  const mounted = useRef(true);
  const lastKeyRef = useRef<string | null>(null);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    // Collapse StrictMode's dev double-invoke (matches use-cases.ts).
    const key = `${reloadKey}|${caseId}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    const id = ++reqId.current;
    setLoading(true);
    setError(null);
    list(caseId)
      .then((result) => {
        if (!mounted.current || reqId.current !== id) return;
        setRows(result);
      })
      .catch((err) => {
        if (!mounted.current || reqId.current !== id) return;
        setRows([]);
        setError(err instanceof ApiError ? err.message : "Failed to load.");
      })
      .finally(() => {
        if (mounted.current && reqId.current === id) setLoading(false);
      });
  }, [caseId, list, reloadKey]);

  return { rows, loading, error, reload };
}
